import { FC, PropsWithChildren } from 'react';
import { Interpolation, Theme, css } from '@emotion/react';
import { breakpoints } from '@frontend/responsiveness';
import { useScopedAppFlagStore } from '@frontend/scope';
import { useIsSmallMode } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { featureFlags } from '../../../../feature-flags';
import { useCallIntelLocations } from '../../hooks/use-call-intel-locations';
import { FilterNotifications } from '../atoms/filter-notifications';
import { CallIntelFiltersProvider } from '../context/call-intel-filters.provider';
import { ChipBar } from '../molecules/chip-bar';
import { FilterTrayToggle } from '../molecules/filter-tray-section/filter-tray-toggle';
import { LocationFilterChip } from '../molecules/location-filter/location-filter-chip';
import { LocationFilterTray } from '../molecules/location-filter/location-filter-tray';
import { OfficeHoursFilterTray } from '../molecules/office-hours-filter-tray';
import { OfficeHoursResetBanner } from '../molecules/office-hours-reset-banner';
import { TimePeriodFilterChip } from '../molecules/time-period-filter/time-period-filter-chip';
import { TimePeriodFilterTray } from '../molecules/time-period-filter/time-period-filter-tray';
import { FilterTray } from './filter-tray';

interface CallIntelServiceQualityFiltersRootProps extends PropsWithChildren {
  isLoading?: boolean;
}

const CallIntelServiceQualityFiltersRoot: FC<CallIntelServiceQualityFiltersRootProps> = ({ isLoading, children }) => {
  return (
    <CallIntelFiltersProvider isLoading={isLoading}>
      <div css={styles.root}>{children}</div>
    </CallIntelFiltersProvider>
  );
};

interface FilterProps {
  containerStyles?: Interpolation<Theme>;
}

const Filters: FC<FilterProps> = ({ containerStyles }) => {
  const { isMultiLocation } = useCallIntelLocations();
  const { getFeatureFlagValue } = useScopedAppFlagStore();

  const isOfficeHourFilterEnabled = getFeatureFlagValue(featureFlags.enableOfficeHourFilter);

  return (
    <>
      <div css={styles.filtersContainer}>
        <ChipBar maxVisibleChipsSmallMode={1} containerStyles={[styles.chipBarContents, containerStyles]}>
          {isMultiLocation && <LocationFilterChip />}
          <TimePeriodFilterChip />
        </ChipBar>
      </div>

      <FilterTray>
        {isMultiLocation && <LocationFilterTray />}
        <TimePeriodFilterTray />
        {isOfficeHourFilterEnabled && <OfficeHoursFilterTray />}
      </FilterTray>

      <FilterNotifications>
        <OfficeHoursResetBanner />
      </FilterNotifications>
    </>
  );
};

interface ActionProps extends PropsWithChildren {
  showTrayFilters?: boolean;
  containerStyles?: Interpolation<Theme>;
}

const Actions: FC<ActionProps> = ({ children, showTrayFilters = true, containerStyles }) => {
  const { isSmallMode } = useIsSmallMode();

  return (
    <div css={[styles.actionsContainer, containerStyles]}>
      {children}
      {showTrayFilters && !isSmallMode && <FilterTrayToggle css={styles.trayButton} />}
    </div>
  );
};

export const CallIntelServiceQualityFilters = Object.assign(CallIntelServiceQualityFiltersRoot, {
  Filters,
  Actions,
});

const styles = {
  root: css`
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.spacing(2)};
    align-items: center;
  `,
  filtersContainer: css`
    display: contents;
  `,
  chipBarContents: css`
    display: contents;
    @container (max-width: ${breakpoints.xsmall.max}px) {
      display: flex;
    }
  `,
  actionsContainer: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
    margin-left: auto;
    @container (max-width: ${breakpoints.xsmall.max}px) {
      margin-left: 0;
    }
  `,
  trayButton: css`
    padding: ${theme.spacing(0.5)};
    height: ${theme.spacing(4)};
    width: ${theme.spacing(4)};
  `,
};
